// Security Features Showcase Component
import React from 'react';
import { Shield, Lock, Eye, Bell, FileCheck, Users } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';

export function SecurityFeatures() {
  const features = [
    {
      icon: Shield,
      title: 'Copy Protection',
      description: 'Advanced protection prevents unauthorized copying, screenshots, and printing of your work.',
      color: 'text-blue-600',
      bg: 'bg-blue-50'
    },
    {
      icon: Eye,
      title: 'Watermark Tracking',
      description: 'Every shared document includes invisible watermarks with collaborator information for accountability.',
      color: 'text-green-600',
      bg: 'bg-green-50'
    },
    {
      icon: Bell,
      title: 'Activity Monitoring',
      description: 'Get instant notifications when collaborators attempt to copy or share your protected content.',
      color: 'text-purple-600',
      bg: 'bg-purple-50'
    },
    {
      icon: FileCheck,
      title: 'Blockchain Timestamps',
      description: 'Immutable proof of ownership with blockchain-verified creation dates and timestamps.',
      color: 'text-orange-600',
      bg: 'bg-orange-50'
    },
    {
      icon: Lock,
      title: 'Role-Based Access',
      description: 'Granular permission controls - decide who can view, comment, or edit your documents.',
      color: 'text-red-600',
      bg: 'bg-red-50'
    },
    {
      icon: Users,
      title: 'Secure Collaboration',
      description: 'Share confidently knowing only the original creator can invite additional collaborators.',
      color: 'text-indigo-600',
      bg: 'bg-indigo-50'
    }
  ];

  return (
    <div className="py-16 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            <Shield className="h-4 w-4" />
            Enterprise-Grade Security
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Your Work. Protected.
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Share your creative work with confidence. Our multi-layer security system
            protects your intellectual property from unauthorized copying and theft.
          </p>
        </div>

        {/* Feature Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <Card key={index} className="border-2 hover:border-purple-200 transition-all hover:shadow-lg">
              <CardHeader>
                <div className={`w-12 h-12 ${feature.bg} rounded-lg flex items-center justify-center mb-4`}>
                  <feature.icon className={`h-6 w-6 ${feature.color}`} />
                </div>
                <CardTitle className="text-lg">{feature.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-gray-600">
                  {feature.description}
                </CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Trust Indicators */}
        <div className="mt-16 bg-gradient-to-r from-purple-50 to-blue-50 rounded-2xl p-8 border border-purple-100">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-purple-600 mb-2">256-bit</div>
              <div className="text-gray-600">Encryption Standard</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-purple-600 mb-2">100%</div>
              <div className="text-gray-600">Blockchain Verified</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-purple-600 mb-2">24/7</div>
              <div className="text-gray-600">Activity Monitoring</div>
            </div>
          </div>
        </div>

        {/* How It Works */}
        <div className="mt-16">
          <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">
            How Content Protection Works
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[
              { step: '1', title: 'Create', desc: 'Write your document with AI assistance' },
              { step: '2', title: 'Timestamp', desc: 'Automatic blockchain verification' },
              { step: '3', title: 'Share Safely', desc: 'Invite collaborators with role controls' },
              { step: '4', title: 'Monitor', desc: 'Track all access and copy attempts' }
            ].map((step, i) => (
              <div key={i} className="relative">
                <div className="bg-white border-2 border-purple-100 rounded-lg p-6 text-center">
                  <div className="w-12 h-12 bg-purple-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                    {step.step}
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">{step.title}</h4>
                  <p className="text-sm text-gray-600">{step.desc}</p>
                </div>
                {i < 3 && (
                  <div className="hidden md:block absolute top-1/2 -right-2 w-4 h-4 border-t-2 border-r-2 border-purple-300 transform rotate-45 -translate-y-1/2"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function SecurityBadge({ variant = 'full' }) {
  if (variant === 'compact') {
    return (
      <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-semibold">
        <Shield className="h-3 w-3" />
        <span>Protected</span>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0">
          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
            <Shield className="h-5 w-5 text-green-600" />
          </div>
        </div>
        <div>
          <h4 className="font-semibold text-gray-900 mb-1">Content Protected</h4>
          <p className="text-sm text-gray-600">
            This document is secured with copy protection, watermarking, and activity monitoring.
          </p>
        </div>
      </div>
    </div>
  );
}
